/* eslint-disable i18next/no-literal-string */
import { useState, useMemo } from "react"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"
import { Box, Typography } from "@strapi/design-system"
import {
  MetricsChartProps,
  WidgetAnalyticsItem,
  ProductAnalyticsItem,
} from "../../../types/analytics"
import dayjs from "dayjs"

type MetricType =
  | "app_opened"
  | "unique_users"
  | "engaged_users"
  | "vto_events"
  | "vto_users"
  | "add_to_cart"
  | "add_to_cart_users"
type FilterType = "all" | string // 'all' or specific product name

interface ChartDataPoint {
  date: string
  value: number
  formattedDate: string
}

export const MetricsChart: React.FC<MetricsChartProps> = ({
  analyticsItems,
  productAnalytics,
  showOverlay = false,
}) => {
  const [selectedMetric, setSelectedMetric] = useState<MetricType>("vto_events")
  const [selectedProduct, setSelectedProduct] = useState<FilterType>("all")

  const uniqueProducts = useMemo(() => {
    const products = new Set(
      productAnalytics
        .map((item) => item.product_title)
        .filter((title) => typeof title === "string" && title.trim().length > 0)
    )

    return Array.from(products).sort()
  }, [productAnalytics])
  const chartData = useMemo((): ChartDataPoint[] => {
    let dataSource: (WidgetAnalyticsItem | ProductAnalyticsItem)[] = []

    if (selectedProduct === "all") dataSource = analyticsItems
    else dataSource = productAnalytics.filter((item) => item.product_title === selectedProduct)

    const groupedData = dataSource.reduce((acc, item) => {
      const date = item.date
      if (!acc[date]) acc[date] = 0

      acc[date] += item[selectedMetric] || 0
      return acc
    }, {} as Record<string, number>)
    return Object.entries(groupedData)
      .map(([date, value]) => ({
        date,
        value,
        formattedDate: dayjs(date).format("MMM D"),
      }))
      .sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix())
      .slice(-30)
  }, [analyticsItems, productAnalytics, selectedMetric, selectedProduct])

  const metricOptions = [
    { value: "app_opened", label: "App Opened Events" },
    { value: "unique_users", label: "Unique Users" },
    { value: "engaged_users", label: "Engaged Users" },
    { value: "vto_events", label: "VTO Events" },
    { value: "vto_users", label: "VTO Users" },
    { value: "add_to_cart", label: "Add to Cart Events" },
    { value: "add_to_cart_users", label: "Add to Cart Users" },
  ]

  const getMetricColor = (metric: MetricType): string => {
    const colors = {
      app_opened: "#3b82f6",
      unique_users: "#8b5cf6",
      engaged_users: "#06b6d4",
      vto_events: "#10b981",
      vto_users: "#f59e0b",
      add_to_cart: "#ef4444",
      add_to_cart_users: "#ec4899",
    }
    return colors[metric] || "#6b7280"
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]
      return (
        <div className="bg-gray-800 p-3 rounded-lg border border-gray-600">
          <div className="font-semibold text-white">{label}</div>
          <div className="text-sm text-blue-400">
            {metricOptions.find((opt) => opt.value === selectedMetric)?.label}:{" "}
            {data.value.toLocaleString()}
          </div>
        </div>
      )
    }
    return null
  }

  const totalValue = chartData.reduce((sum, item) => sum + item.value, 0)
  const averageValue = chartData.length > 0 ? totalValue / chartData.length : 0
  const maxValue = Math.max(...chartData.map((item) => item.value))
  const minValue = Math.min(...chartData.map((item) => item.value))

  if (showOverlay)
    return (
      <Box padding={4} background="neutral100" borderRadius="4px" className="min-h-[400px]">
        <Typography variant="alpha" fontWeight="semiBold" marginBottom={2}>
          Metrics Analysis - Loading...
        </Typography>
        <Box className="h-[300px] bg-gray-100 rounded" />
      </Box>
    )

  return (
    <div className="p-6 bg-dark-secondary rounded-lg border border-gray-700">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Detailed Metrics</h3>
            <p className="text-sm text-gray-300">
              Interactive time-series analysis with product filtering
            </p>
          </div>

          <div className="flex gap-3 items-center flex-wrap">
            <div className="min-w-[200px]">
              <label className="block text-sm font-semibold text-white mb-1">Metric</label>
              <select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value as MetricType)}
                className="w-full p-2 bg-gray-800 border border-gray-600 rounded text-white"
              >
                {metricOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="min-w-[200px]">
              <label className="block text-sm font-semibold text-white mb-1">Product Filter</label>
              <select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value as FilterType)}
                className="w-full p-2 bg-gray-800 border border-gray-600 rounded text-white"
              >
                <option value="all">All Products</option>
                {uniqueProducts.map((product) => (
                  <option key={product} value={product} title={product}>
                    {product.length > 25 ? product.substring(0, 25) + "..." : product}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex gap-6 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-white">Total</div>
            <div className="text-xl font-bold text-white">{totalValue.toLocaleString()}</div>
          </div>

          <div>
            <div className="text-sm font-semibold text-white">Daily Average</div>
            <div className="text-xl font-bold text-white">{averageValue.toFixed(0)}</div>
          </div>

          <div>
            <div className="text-sm font-semibold text-white">Peak Day</div>
            <div className="text-xl font-bold text-white">{maxValue.toLocaleString()}</div>
          </div>

          <div>
            <div className="text-sm font-semibold text-white">Lowest Day</div>
            <div className="text-xl font-bold text-white">{minValue.toLocaleString()}</div>
          </div>
        </div>

        <div className="h-[400px]">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="formattedDate"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#9ca3af" }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#9ca3af" }}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip content={<CustomTooltip />} />

                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={getMetricColor(selectedMetric)}
                  strokeWidth={3}
                  dot={{ fill: getMetricColor(selectedMetric), strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: getMetricColor(selectedMetric) }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center">
              <p className="text-gray-300">No data available for the selected filters</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
